/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Target, Code, Users, Shield, Zap, Globe } from 'lucide-react';

export const Introduction = () => (
  <div className="space-y-8 animate-fade-in">
    <p className="text-xl text-stone-600 dark:text-stone-300 leading-relaxed font-light">
      GhostOS is a high-performance financial operating system bridging digital assets with real-world commerce. Built on the <span className="text-stone-900 dark:text-white font-bold">Ghost Protocol</span>, it provides an isolated runtime for private, secure, and sovereign financial interactions on Solana and Sui.
    </p>
    
    <div className="grid gap-6 md:grid-cols-2 mt-12">
        <div className="p-8 bg-stone-50 dark:bg-white/[0.03] rounded-card border border-stone-200 dark:border-white/10 group hover:border-nobel-gold/20 transition-all">
            <h3 className="font-serif text-xl font-bold text-stone-900 dark:text-white mb-4 flex items-center gap-3">
                <Shield size={20} className="text-nobel-gold" /> Sovereign Philosophy
            </h3>
            <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed">
                GhostOS is built on the principle of absolute sovereignty. By utilizing zero-knowledge proofs for metadata and stealth addresses for settlement, we ensure your financial footprint remains strictly your own.
            </p>
        </div>
        <div className="p-8 bg-stone-50 dark:bg-white/[0.03] rounded-card border border-stone-200 dark:border-white/10 group hover:border-nobel-gold/20 transition-all">
            <h3 className="font-serif text-xl font-bold text-stone-900 dark:text-white mb-4 flex items-center gap-3">
                <Zap size={20} className="text-nobel-gold" /> Atomic Efficiency
            </h3>
            <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed">
                Legacy systems take days to settle. GhostOS settles in milliseconds. Our stack leverages the sub-second finality of modern blockchains to provide a retail experience that feels like fiat, but operates on pure code.
            </p>
        </div>
    </div>

    <div className="pt-12">
        <h3 className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.4em] mb-10 text-center">User Archetypes</h3>
        <div className="grid gap-6 md:grid-cols-3">
            <div className="p-8 bg-white dark:bg-[#15171C] rounded-card border border-stone-100 dark:border-white/5 shadow-sm hover:shadow-xl transition-all group">
                <div className="w-12 h-12 bg-stone-900 dark:bg-white rounded-xl flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] mb-6 group-hover:scale-110 transition-transform">
                    <Users size={24} />
                </div>
                <h4 className="font-bold text-stone-900 dark:text-white mb-2">Merchants</h4>
                <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">Deploy high-throughput POS terminals and titanium hardware to accept crypto globally with 0.1% fees.</p>
            </div>
             <div className="p-8 bg-white dark:bg-[#15171C] rounded-card border border-stone-100 dark:border-white/5 shadow-sm hover:shadow-xl transition-all group">
                <div className="w-12 h-12 bg-stone-900 dark:bg-white rounded-xl flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] mb-6 group-hover:scale-110 transition-transform">
                    <Code size={24} />
                </div>
                <h4 className="font-bold text-stone-900 dark:text-white mb-2">Developers</h4>
                <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">Integrate the Ghost SDK for autonomous AI agent payments, x402 micropayments, and cross-chain routing.</p>
            </div>
             <div className="p-8 bg-white dark:bg-[#15171C] rounded-card border border-stone-100 dark:border-white/5 shadow-sm hover:shadow-xl transition-all group">
                <div className="w-12 h-12 bg-stone-900 dark:bg-white rounded-xl flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] mb-6 group-hover:scale-110 transition-transform">
                    <Globe size={24} />
                </div>
                <h4 className="font-bold text-stone-900 dark:text-white mb-2">Institutions</h4>
                <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">Manage private treasury flows, generate cryptographically signed audits, and issue white-labeled card programs.</p>
            </div>
        </div>
    </div>
  </div>
);